import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import { Megaphone, Plus, Pencil, Trash2, X, Save, Upload, Loader2, ChevronRight } from "lucide-react";

const CARTOON = {
  rc: "https://static.prod-images.emergentagent.com/jobs/80d61861-c1ca-4734-bc3d-d13d8faf2a1b/images/aec6a8dae258a0b0516e76000aaf84da9290f29ba47f72f2a165528386de565b.jpeg",
  sim: "https://static.prod-images.emergentagent.com/jobs/80d61861-c1ca-4734-bc3d-d13d8faf2a1b/images/739d1dcc5cf8ba1f6ddf77fde5e74a8bb59b2e7fd59456b44a143b5a85f995b6.jpeg",
};
const DEFAULT_CARTOON = "https://static.prod-images.emergentagent.com/jobs/80d61861-c1ca-4734-bc3d-d13d8faf2a1b/images/edb6ee79ad019ec08facb38221e55058fc8bb48f918a2987df704147b3eb3aac.jpeg";

const imgFor = (a) => a.image_url || CARTOON[a.category] || DEFAULT_CARTOON;
const TYPE_LABEL = { championship: "Campionato", event: "Gran Premio", custom: "Locandina" };
const EMPTY = { title: "", subtitle: "", meta: "", category: "", image_url: "", link_path: "", cost: 0 };

export default function Locandine() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    setError(false);
    return api.get("/announcements/all")
      .then((r) => setItems(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (a) => setEditing({
    id: a.id, title: a.title || "", subtitle: a.subtitle || "", meta: a.meta || "",
    category: a.category || "", image_url: a.image_url || "", link_path: a.link_path || "", cost: a.cost || 0,
  });
  const set = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.value }));

  const uploadFile = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setEditing((s) => ({ ...s, image_url: `${API}/uploads/file/${data.id}` }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error("Errore nel caricamento immagine");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(), subtitle: editing.subtitle, meta: editing.meta,
        category: editing.category, image_url: editing.image_url, link_path: editing.link_path,
        cost: parseInt(editing.cost || 0, 10),
      };
      if (editing.id) await api.put(`/announcements/${editing.id}`, payload);
      else await api.post("/announcements", payload);
      toast.success(editing.id ? "Locandina aggiornata" : "Locandina creata");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const del = async (a) => {
    if (!window.confirm("Eliminare questa locandina?")) return;
    try { await api.delete(`/announcements/${a.id}`); toast.success("Locandina eliminata"); load(); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <BackButton className="mb-6" />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded bg-yellow-400/10 border border-yellow-400/40">
            <Megaphone className="h-5 w-5 text-yellow-400" />
          </span>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tighter text-zinc-50">Locandine</h1>
            <p className="text-sm text-zinc-400">Annunci e locandine di campionati, Gran Premi e comunicazioni.</p>
          </div>
        </div>
        {isAdmin && (
          <button onClick={openNew} data-testid="new-locandina-btn"
            className="inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3 hover:bg-yellow-300 transition-colors duration-200">
            <Plus className="h-4 w-4" /> Nuova locandina
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-zinc-500 py-16 text-center" data-testid="loading-locandine">Caricamento locandine…</p>
      ) : error ? (
        <p className="text-red-400 py-16 text-center" data-testid="error-locandine">Impossibile caricare le locandine. Riprova più tardi.</p>
      ) : items.length === 0 ? (
        <p className="text-zinc-500 py-16 text-center" data-testid="empty-locandine">Nessuna locandina ancora.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locandine-grid">
          {items.map((a, i) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -6 }} transition={{ delay: Math.min(i * 0.04, 0.3), duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`locandina-${a.id}`}
              className="group relative border border-zinc-800 bg-zinc-900 overflow-hidden hover:border-yellow-400/50 transition-colors duration-200">
              <div className="relative h-44 overflow-hidden">
                <img src={imgFor(a)} alt={a.title} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-transparent" />
                <span className="absolute top-3 left-3 text-[9px] font-bold tracking-[0.2em] uppercase bg-zinc-950/70 text-cyan-400 px-2 py-1">
                  {TYPE_LABEL[a.type] || "Locandina"}
                </span>
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button onClick={() => openEdit(a)} data-testid={`edit-locandina-${a.id}`} title="Modifica"
                      className="h-8 w-8 flex items-center justify-center rounded-full bg-zinc-950/80 text-cyan-400 hover:text-cyan-300 transition-colors duration-200">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => del(a)} data-testid={`del-locandina-${a.id}`} title="Elimina"
                      className="h-8 w-8 flex items-center justify-center rounded-full bg-zinc-950/80 text-red-400 hover:text-red-300 transition-colors duration-200">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold uppercase text-zinc-50">{a.title}</h3>
                {a.subtitle && <p className="mt-0.5 text-sm font-bold text-yellow-400">{a.subtitle}</p>}
                {a.meta && <p className="mt-2 text-sm text-zinc-400">{a.meta}</p>}
                {a.cost > 0 && <p className="mt-3 inline-flex items-center border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-xs font-bold px-3 py-1.5 rounded-full">Quota €{a.cost}</p>}
                {a.link_path && (
                  <Link to={a.link_path} data-testid={`goto-locandina-${a.id}`}
                    className="mt-4 inline-flex items-center gap-1 text-xs font-bold tracking-[0.15em] uppercase text-cyan-400 hover:gap-2 transition-all duration-200">
                    Scopri <ChevronRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 bg-zinc-950/85 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto border border-zinc-800 bg-zinc-900 p-6" data-testid="locandina-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-bold uppercase text-zinc-50">{editing.id ? "Modifica locandina" : "Nuova locandina"}</h3>
              <button onClick={() => setEditing(null)} data-testid="locandina-modal-close" className="text-zinc-400 hover:text-zinc-100 transition-colors duration-200"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Titolo *</label>
                <input value={editing.title} onChange={set("title")} data-testid="locandina-title" placeholder="Titolo della locandina"
                  className="mt-2 w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-2.5 text-zinc-100 text-sm transition-colors duration-200" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Sottotitolo</label>
                <input value={editing.subtitle} onChange={set("subtitle")} data-testid="locandina-subtitle" placeholder="es. Categoria o slogan"
                  className="mt-2 w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-2.5 text-zinc-100 text-sm transition-colors duration-200" />
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Testo informativo</label>
                <textarea value={editing.meta} onChange={set("meta")} data-testid="locandina-meta" rows={2} placeholder="es. data · luogo · orario"
                  className="mt-2 w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-2.5 text-zinc-100 text-sm transition-colors duration-200" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Tema immagine</label>
                  <select value={editing.category} onChange={set("category")} data-testid="locandina-category"
                    className="mt-2 w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-2.5 text-zinc-100 text-sm transition-colors duration-200">
                    <option value="">Generico</option>
                    <option value="rc">RC</option>
                    <option value="sim">Simulatore</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Quota (€)</label>
                  <input type="number" min="0" value={editing.cost} onChange={set("cost")} data-testid="locandina-cost" placeholder="0"
                    className="mt-2 w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-2.5 text-zinc-100 text-sm transition-colors duration-200" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Immagine</label>
                <div className="mt-2 flex items-center gap-3">
                  <img src={editing.image_url || CARTOON[editing.category] || DEFAULT_CARTOON} alt="" className="h-16 w-24 object-cover border border-zinc-700" />
                  <input ref={fileRef} type="file" accept="image/*" onChange={uploadFile} className="hidden" data-testid="locandina-file" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="locandina-upload-btn"
                    className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-200 text-xs font-bold uppercase px-3 py-2 hover:border-zinc-500 transition-colors duration-200">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica foto
                  </button>
                  {editing.image_url && (
                    <button onClick={() => setEditing((s) => ({ ...s, image_url: "" }))} data-testid="locandina-clear-img"
                      className="text-xs text-zinc-500 hover:text-red-400 transition-colors duration-200">Usa cartoon</button>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-zinc-500">Se non carichi un'immagine, viene usata la locandina cartoon in base al tema scelto.</p>
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-zinc-400">Link (facoltativo)</label>
                <input value={editing.link_path} onChange={set("link_path")} data-testid="locandina-link" placeholder="es. /campionati/ID"
                  className="mt-2 w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-2.5 text-zinc-100 text-sm transition-colors duration-200" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors duration-200">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="save-locandina-btn"
                className="inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3 hover:bg-yellow-300 transition-colors duration-200 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
